import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { ApiError } from '../../../../lib/api';
import {
  CLUB_PROFILE_FIELDS,
  CLUB_PROFILE_FIELD_MAX,
  CLUB_PROFILE_LABELS,
  EMPTY_CLUB_PROFILE,
  type ClubProfileValues,
} from '../../../../lib/clubProfile';
import { socialApi } from './socialApi';
import { SocialFrame } from './SocialFrame';

// /portal/social/profile — what the club tells the AI about itself.
// Optional: nothing in KyckSocial needs it to work, it only improves the copy.
// The page says it is the CLUB'S statement and shows when it was last edited,
// because stale facts are the main failure once the model stops inventing.

function ProfileForm({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['social-profile', accountId],
    queryFn: () => socialApi.getProfile(accountId),
    retry: false,
  });
  const [draft, setDraft] = useState<ClubProfileValues>(EMPTY_CLUB_PROFILE);
  useEffect(() => {
    if (q.data) setDraft(q.data.values);
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => socialApi.putProfile(accountId, draft),
    onSuccess: (dto) => qc.setQueryData(['social-profile', accountId], dto),
  });

  if (q.isPending) return <CenteredSpinner />;
  if (q.isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {q.error instanceof ApiError && q.error.status === 404
          ? 'That location was not found.'
          : 'Could not load the club profile. Try again.'}
      </p>
    );
  }
  return (
    <form
      className="max-w-2xl space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <p className="text-sm text-muted-foreground">
        This is what <strong>you</strong> tell our AI about your club when it drafts posts and replies. Keep it current:
        it is treated as your statement, not as checked fact. Leave anything blank.{' '}
        <strong>Do not add opening hours:</strong> the AI is told not to state them, because your Google and Facebook pages
        are where the public checks.
      </p>
      {q.data.updatedAt && (
        <p className="text-xs text-muted-foreground" data-testid="profile-updated">
          Last edited {q.data.updatedAt.slice(0, 10)}
          {q.data.updatedBy ? ` by ${q.data.updatedBy}` : ''}.
        </p>
      )}
      {CLUB_PROFILE_FIELDS.map((f) => (
        <label key={f} className="block">
          <span className="text-sm font-medium text-foreground">{CLUB_PROFILE_LABELS[f].label}</span>
          <span className="block text-xs text-muted-foreground">{CLUB_PROFILE_LABELS[f].hint}</span>
          <textarea
            className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm text-foreground"
            rows={3}
            maxLength={CLUB_PROFILE_FIELD_MAX}
            value={draft[f] ?? ''}
            onChange={(e) => setDraft({ ...draft, [f]: e.target.value === '' ? null : e.target.value })}
          />
        </label>
      ))}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {save.isSuccess && <span className="text-sm text-muted-foreground">Saved.</span>}
        {save.isError && (
          <span className="text-sm text-destructive" role="alert">
            {save.error instanceof Error ? save.error.message : 'Could not save.'}
          </span>
        )}
      </div>
    </form>
  );
}

export function ClubProfilePage() {
  return <SocialFrame tab="profile">{(accountId) => <ProfileForm key={accountId} accountId={accountId} />}</SocialFrame>;
}
